import { BaseApi } from '@shared/config';
import type {
  Candidate,
  CandidateProcessListResponse,
  CandidateRequestParams,
  UpdateCandidateRequest,
  CandidateStatus,
  CandidateResume,
  CandidateResumeListResponse,
  CandidateProcessRequestParams,
  UpdateCandidateResumeRequest,
  UpdateCandidateStatusRequest,
  ChangeCandidateResumeActivityRequest,
  CandidateResumeActivityResponse,
} from '../types';
import {
  $CandidateProcessListResponse,
  $CandidateResume,
  $CandidateResumeActivity,
  $CandidateResumeListResponse,
  $CandidateStatus,
} from '../schemas';

class CandidatesApi extends BaseApi {
  getCandidates = <T>(params: CandidateRequestParams): Promise<T> => {
    return this.instance.get('candidates', { params });
  };

  getCandidate = (id: string): Promise<Candidate> => {
    return this.instance.get(`candidates/${id}`);
  };

  getCandidateResumes = (
    candidateId: number,
    params: CandidateRequestParams
  ): Promise<CandidateResumeListResponse> => {
    return this.instance
      .get(`candidates/${candidateId}/resumes`, { params })
      .then($CandidateResumeListResponse.parse);
  };

  getCandidateResume = (
    candidateId: string,
    resumeId: string
  ): Promise<CandidateResume> => {
    return this.instance
      .get(`candidates/${candidateId}/resumes/${resumeId}`)
      .then($CandidateResume.parse);
  };

  updateCandidateResume = (
    candidateId: number,
    resumeId: number,
    request: UpdateCandidateResumeRequest
  ): Promise<unknown> => {
    return this.instance.put(
      `candidates/${candidateId}/resumes/${resumeId}`,
      request
    );
  };

  getCandidateProcesses = (
    id: string,
    params: CandidateProcessRequestParams,
    signal?: AbortSignal
  ): Promise<CandidateProcessListResponse> => {
    return this.instance
      .get(`candidates/${id}/processes`, { params, signal })
      .then($CandidateProcessListResponse.parse);
  };

  updateCandidateStatus = (
    id: string | number,
    data: UpdateCandidateStatusRequest
  ): Promise<Candidate> => {
    return this.instance.put(`candidates/${id}/status`, data);
  };

  changeCandidateResumeActivity = (
    resumeId: number,
    request: ChangeCandidateResumeActivityRequest
  ): Promise<CandidateResumeActivityResponse> => {
    return this.instance
      .put(`candidates/resumes/${resumeId}/change-activity`, request)
      .then($CandidateResumeActivity.parse);
  };

  updateCandidate = (
    id: string,
    data: UpdateCandidateRequest
  ): Promise<Candidate> => {
    return this.instance.put(`candidates/${id}`, data);
  };

  updateCandidateInterview = (id: string): Promise<Candidate> => {
    return this.instance.put(`candidates/${id}/interview-required`);
  };

  updateCandidateEmploymentDate = (
    id: string,
    data: Pick<UpdateCandidateRequest, 'employmentDate'>
  ): Promise<Candidate> => {
    return this.instance.put(`candidates/${id}`, data);
  };

  sendMessage = (id: string, data: string | File, type: 'text' | 'audio') => {
    const messageType = {
      text: 'messageText',
      audio: 'messageFile',
    };

    if (type === 'text') {
      return this.instance.post(
        `candidates/${id}/message`,
        {
          [messageType[type]]: data,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    }

    if (type === 'audio' && data instanceof File) {
      const formData = new FormData();
      formData.append(messageType[type], data);

      return this.instance.post(`candidates/${id}/message`, formData, {
        headers: {},
        transformRequest: data => data,
      });
    }

    throw new Error(`Invalid data type for ${type} message`);
  };

  getStatuses = (): Promise<CandidateStatus[]> => {
    return this.instance
      .get('candidates/statuses')
      .then($CandidateStatus.array().parse);
  };

  exportCandidates = ({
    dateFrom,
    dateTo,
  }: {
    dateFrom: string | null;
    dateTo?: string | null;
  }) => {
    return this.instance.post(
      `candidates/export`,
      {
        data: dateFrom &&
          dateTo && {
            dateFrom,
            dateTo,
          },
      },
      {
        responseType: 'blob',
      }
    );
  };
}

export const candidatesApi = new CandidatesApi();
